import { HttpClient, HttpClientModule } from '@angular/common/http';
import { ApplicationConfig, ErrorHandler as AngularErrorHandler, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { PreloadAllModules, RouteReuseStrategy, withPreloading, provideRouter } from '@angular/router';
import { provideIonicAngular, IonicRouteStrategy, NavController } from '@ionic/angular/standalone';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { AppAndSharedTranslateLoader } from '@shared/utils/translate/app-and-shared-translate-loader';
import { SHARED_TRANSLATION_DOMAINS } from '@shared/utils/translate/translation-domains';
import { AiService } from '@shared/services/ai/ai.service';
import { AlgaecideAiService } from '@shared/services/ai/algaecide-ai.service';
import { AlkalinityPhAiService } from '@shared/services/ai/alkalinity-ph-ai.service';
import { ChlorineAiService } from '@shared/services/ai/chlorine-ai.service';
import { DecanterAiService } from '@shared/services/ai/decanter-ai.service';
import { MachineLearningService } from '@shared/services/ai/machine-learning.service';
import { AnalyzeHistoryService } from '@shared/services/analyze/analyze-history.service';
import { AnalyzeParametersService } from '@shared/services/analyze/analyze-parameters.service';
import { AnalyzePhotosService } from '@shared/services/analyze/analyze-photos.service';
import { AnalyzeService } from '@shared/services/analyze/analyze.service';
import { AplicationService } from '@shared/services/aplication/aplication.service';
import { AuthenticationService } from '@shared/services/authentication/authentication.service';
import { CameraPreviewService } from '@shared/services/camera/camera-preview.service';
import { ChatService } from '@shared/services/chat/chat.service';
import { ClockAndVisitOperationService } from '@shared/services/clock-and-visit/clock-and-visit-operation.service';
import { ClockInService } from '@shared/services/clock-and-visit/clock-in.service';
import { ChooserService } from '@shared/services/chooser/chooser.service';
import { FileService } from '@shared/services/file/file.service';
import { GeographicPositionService } from '@shared/services/geolocation/geographic-position.service';
import { GeolocationService } from '@shared/services/geolocation/geolocation.service';
import { GoogleMapsService } from '@shared/services/geolocation/google-maps.service';
import { GraphqlService } from '@shared/services/http/graphql.service';
import { LocalService } from '@shared/services/local/local.service';
import { LogService } from '@shared/services/log/log.service';
import { NativeStorageService } from '@shared/services/storage/native-storage.service';
import { NavControllerService } from '@shared/services/navigation/nav-controller.service';
import { NotificationService } from '@shared/services/notification/notification.service';
import { OfferService } from '@shared/services/offer/offer.service';
import { PhotoService } from '@shared/services/photo/photo.service';
import { PoolService } from '@shared/services/pool/pool.service';
import { PopoverService } from '@shared/services/popover/popover.service';
import { ProductService } from '@shared/services/product/product.service';
import { QuestionnaireService } from '@shared/services/questionnaire/questionnaire.service';
import { SugestionService } from '@shared/services/sugestion/sugestion.service';
import { TransferenceService } from '@shared/services/transference/transference.service';
import { UserService } from '@shared/services/user/user.service';
import { UtilsService } from '@shared/services/utils/utils.service';
import { VisitService } from '@shared/services/visit/visit.service';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { environment as ENV } from '../environments/environment';
import { ErrorHandler } from './ErrorHandler';
import { appRoutes } from './app.routes';

import { TelephoneService } from 'src/app/services/telephone-service';
import { SuggestionService } from 'src/app/services/suggestion.service';
import { PromotionService } from 'src/app/services/promotion.service';
import { PRO_APP_TRANSLATION_DOMAINS } from './translation-domains';
import { provideAppLocale } from '@shared/services/locale/locale.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection(),
    provideIonicAngular(),
    provideRouter(appRoutes, withPreloading(PreloadAllModules)),
    importProvidersFrom(
      HttpClientModule,
      TranslateModule.forRoot({
        defaultLanguage: 'pt',
        loader: {
          provide: TranslateLoader,
          useFactory: (http: HttpClient) => new AppAndSharedTranslateLoader(http, {
            sharedDomains: SHARED_TRANSLATION_DOMAINS,
            appDomains: PRO_APP_TRANSLATION_DOMAINS,
            suffix: `.json?v=${ENV.version}`,
          }),
          deps: [HttpClient],
        },
      }),
    ),
    provideEnvironmentNgxMask(),
    ...provideAppLocale(),
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    { provide: AngularErrorHandler, useClass: ErrorHandler },
    GeolocationService,
    AuthenticationService,
    LocalService,
    GoogleMapsService,
    VisitService,
    ProductService,
    TransferenceService,
    AnalyzeService,
    PhotoService,
    SugestionService,
    NotificationService,
    GeographicPositionService,
    PoolService,
    ClockInService,
    ClockAndVisitOperationService,
    LogService,
    QuestionnaireService,
    UtilsService,
    GraphqlService,
    UserService,
    AiService,
    AnalyzeHistoryService,
    AnalyzePhotosService,
    CameraPreviewService,
    ChlorineAiService,
    MachineLearningService,
    AlkalinityPhAiService,
    AplicationService,
    DecanterAiService,
    AlgaecideAiService,
    NavControllerService,
    { provide: NavController, useExisting: NavControllerService },
    ChatService,
    OfferService,
    FileService,
    PopoverService,
    NativeStorageService,
    AnalyzeParametersService,
    TelephoneService,
    SuggestionService,
    PromotionService,
    ChooserService,
  ],
};
